/* eslint-disable camelcase */
import Vue from 'vue'

import { BRAND_PRIORITY } from '@/constants/orderBrands'
import { ORDER_GUEST_DEFAULT_PHONE_NUMBER } from '@/constants/orderGuest'
import { ORDER_TYPES } from '@/constants/orderTypes'
import { PAYMENT_TYPES } from '@/constants/paymentTypes'
import { ORDER_IMMEDIATE_SEND_PERMISSION } from '@/constants/permissions'
import { TIME_TYPES } from '@/constants/timeTypes'
import { getFirstDivisionInfo, getUserData } from '@/core/auth/utils'
import {
    applyBaseCommits,
    callApi,
    canProceedWithIntervals,
    getDishType,
    getRestaurantRkId,
    parseCoord,
} from '@/helpers/intervals/delivery'
import {
    buildIntervalsPayload,
    buildCoordinatesParams,
    buildRestaurantParams,
} from '@/helpers/intervals/deliveryPayloadBuilder'
import { fetchIntervals } from '@/services/deliveryService'
import { fetchDivisions } from '@/services/divisionsService'
import { fetchOrderById, updateOrder } from '@/services/orderService'
import useFormat from '@/views/utils'
import { $themeConfig } from '@themeConfig'

import { orderStore, ticketTimeStore } from '../gql/query'

import actionsContractors from './contractors/actions'
import actionsDish from './dish/actions'
import actionsStopList from './stop-list/actions'

const { formatDate, formatDateForPickr, parseStringToDate, convertToTZformat } =
    useFormat()

const {
    GET_TYPES,
    GET_DISCOUNTS,
    GET_STAFF_DISCOUNTS,
    GET_REGIONS,
    GET_STATUSES,
    GET_DEF_SOURCE,
    GET_CLOSE_OPTIONS,
    UPDATE_PAYMENT_DISCOUNT,
} = orderStore()

const { GET_REASON_TICKET_TIME } = ticketTimeStore()

const { parseStringTime } = useFormat()

const availabilitiesInFlight = new Map()

const normalizeAvailabilityDate = date => {
    const normalizedDate = new Date(date)
    normalizedDate.setSeconds(0, 0)
    return normalizedDate.toISOString()
}

export default {
    // Actions Dish
    ...actionsDish,
    // Actions StopList
    ...actionsStopList,
    // Actions Contractors
    ...actionsContractors,
    // Справочники ------>
    fetchSectors({ commit }) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .get(`${$themeConfig.app.backUrlSectors}/sectors?mod=short`)
                .then(response => {
                    commit('SET_SECTORS', response.data?.sectors || [])
                    resolve()
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    fetchBrands({ state, commit }) {
        return new Promise((resolve, reject) => {
            if (state.brands.length === 0) {
                Vue.axios
                    .get(`${$themeConfig.app.backUrlDivisions}/brands`)
                    .then(response => {
                        commit('SET_BRANDS', response.data)
                        resolve()
                    })
                    .catch(error => {
                        reject(error)
                    })
            } else {
                resolve()
            }
        })
    },

    // загрузка скидок
    async fetchDiscounts({ commit }, { sifrIds = [], isStaff = false } = {}) {
        const response = await Vue.axios.post($themeConfig.app.gqlUrl, {
            query: isStaff ? GET_STAFF_DISCOUNTS : GET_DISCOUNTS,
            variables: isStaff ? { _in: sifrIds } : { _nin: sifrIds },
        })

        commit('ADD_TO_DISCOUNTS', response.data)
    },

    // загрузка типов заказа
    fetchTypes({ commit }) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .post($themeConfig.app.gqlUrl, {
                    query: GET_TYPES,
                    variables: {
                        _incode: [
                            ORDER_TYPES.DELIVERY,
                            ORDER_TYPES.SELF,
                            ORDER_TYPES.SUBDELIVERY,
                            ORDER_TYPES.PREORDER,
                            ORDER_TYPES.STAFF_OWN,
                            ORDER_TYPES.STAFF_OTHER,
                        ],
                    },
                })
                .then(response => {
                    commit('ADD_TO_TYPES', response.data)
                    resolve()
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    fetchGuests(ctx, query) {
        return new Promise((resolve, reject) => {
            if (!query) resolve()
            const getStr = `${$themeConfig.app.backUrl}/guests?limit=10&offset=0&query=${query}`
            Vue.axios
                .get(getStr)
                .then(response => {
                    resolve(response.data)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    fetchRegions({ commit, state }) {
        return new Promise((resolve, reject) => {
            if (state.regions.length === 0) {
                Vue.axios
                    .post($themeConfig.app.gqlUrl, {
                        query: GET_REGIONS,
                        variables: {},
                    })
                    .then(response => {
                        commit('SET_REGIONS', response.data)
                        resolve()
                    })
                    .catch(error => {
                        reject(error)
                    })
            } else {
                resolve()
            }
        })
    },

    async fetchRestaurants({ state, commit }) {
        if (state.restaurants.length) return

        try {
            const response = await fetchDivisions()
            commit('SET_RESTAURANTS', response.data)
        } catch (error) {
            console.error('Ошибка загрузки ресторанов', error)
            throw error
        }
    },

    fetchStatuses({ commit }) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .post($themeConfig.app.gqlUrl, {
                    query: GET_STATUSES,
                    variables: {},
                })
                .then(response => {
                    commit('SET_STATUSES', response.data.data.eds_order_status)
                    resolve(response.data.data.eds_order_status)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    // Причины отмен
    fetchCloseOptions() {
        return new Promise((resolve, reject) => {
            Vue.axios
                .post($themeConfig.app.gqlUrl, {
                    query: GET_CLOSE_OPTIONS,
                    variables: {},
                })
                .then(res => {
                    resolve(res.data)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    fetchDefaultSource(ctx, sourceId = 'callcenter') {
        return new Promise((resolve, reject) => {
            Vue.axios
                .post($themeConfig.app.gqlUrl, {
                    query: GET_DEF_SOURCE,
                    variables: { sourceId },
                })
                .then(response => {
                    resolve(response.data.data.source[0])
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    fetchDefaultBrand() {
        return new Promise((resolve, reject) => {
            Vue.axios
                .get(`${$themeConfig.app.backUrlDivisions}/brands`)
                .then(response => {
                    const brands = response.data

                    const defaultBrand =
                        BRAND_PRIORITY.map(id =>
                            brands.find(brand => brand.id === id)
                        ).find(brand => brand !== undefined) || brands[0]

                    resolve(defaultBrand)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    fetchNotifications({ commit }, id) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .get(
                    `${$themeConfig.app.backUrl}/orders/${id}/notification_events`
                )
                .then(response => {
                    if (response.data)
                        commit('SET_NOTIFICATIONS', response.data)
                    else commit('SET_NOTIFICATIONS', null)
                    resolve()
                })
                .catch(error => {
                    reject(error)
                })
        })
    },
    toggleSendNotification({ getters }, id) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .put(`${$themeConfig.app.backUrl}/orders/${id}`, {
                    send_notification: !getters.getSendNotification,
                })
                .then(response => {
                    resolve(response.data)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    // <------ Справочники
    async createOrder({ dispatch, commit }, options = {}) {
        const { isStaff } = options
        const sourceId = isStaff ? 'delivery_workshop' : 'callcenter'
        const data = await Promise.all([
            dispatch('fetchStatuses'),
            dispatch('fetchDefaultSource', sourceId),
            dispatch('fetchDefaultBrand'),
        ])

        let divisionId = null
        let guest = null
        let orderType = null
        let phone = null

        if (isStaff) {
            divisionId = getFirstDivisionInfo().id

            const guests = await dispatch(
                'fetchGuests',
                ORDER_GUEST_DEFAULT_PHONE_NUMBER
            )
            guest = guests?.[0] ?? null
            phone = ORDER_GUEST_DEFAULT_PHONE_NUMBER
            orderType = { id: 'staff_own', name: 'Для питания', active: null }
        }

        commit('NEW_ORDER', {
            data,
            options: {
                ...options,
                divisionId,
                guest,
                phone,
                order_type: orderType,
            },
        })
    },

    recalcOrder({ commit, getters, state }) {
        const body = {
            dishes: getters.getDishesWithDeleted,
            payments: getters.getPayments,
            promos: getters.getPromosAndDiscounts || [],
        }
        return new Promise(resolve => {
            Vue.axios
                .patch(
                    `${$themeConfig.app.backUrl}/orders/${state.order.id}/recalc`,
                    body
                )
                .then(response => {
                    commit('UPDATE_ORDER_AFTER_RECALC', response.data)
                    commit('SET_HAS_RESEND_DISHES', true)
                    resolve(response.data)
                })
        })
    },

    async addOrder({ commit, getters }) {
        const body = JSON.stringify(getters.getOrderForAdd)
        const response = await Vue.axios.post(
            `${$themeConfig.app.backUrl}/orders`,
            body
        )
        commit('UPDATE_ORDER_AFTER_ADD', response.data)
        commit('SET_DEFAULT_ORIGINAL_ORDER_FIRST_PAYMENT')
        commit('SET_ORIGINAL_CONTRACTOR', null)
        commit('SET_PERMISSIONS', response.data)
    },

    fetchOrder({ commit, dispatch }, id) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .get(`${$themeConfig.app.backUrl}/orders/${id}`)
                .then(response => {
                    commit('SET_ORDER', response.data)
                    commit(
                        'SET_ORIGINAL_ORDER_FIRST_PAYMENT',
                        response.data?.order
                    )
                    commit(
                        'SET_ORIGINAL_CONTRACTOR',
                        response.data?.order?.contractor
                    )
                    commit('SET_PERMISSIONS', response.data)
                    // TODO оч плохо, тк сектора могут еше не подгрузиться
                    dispatch('setSectorById', response.data?.order.sector_id)
                    resolve()
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    updateOrderTime({ getters }, id) {
        const body = {
            delivered_date: getters.getPredictedDeliveryDate,
        }
        return Vue.axios.put(`${$themeConfig.app.backUrl}/orders/${id}`, body)
    },

    async putOrder({ getters, commit }, { id, updateByButtonClick }) {
        commit('SET_WAIT_SAVING', true)

        if (!id) {
            commit('SET_WAIT_SAVING', false)
            throw new Error('Отсутствует id заказа')
        }

        const changes = getters.getOrderForEdit
        commit('RESET_DIRTY_DISHES')

        if (!changes || Object.keys(changes).length === 0) {
            commit('SET_WAIT_SAVING', false)
            return {
                message: {
                    name: 'edit',
                    variant: 'primary',
                    message: 'Заказ не сохранен, так как нет изменений.',
                },
            }
        }

        if (changes.error) {
            commit('SET_WAIT_SAVING', false)
            return {
                message: {
                    name: 'Ошибка',
                    variant: 'danger',
                    message: changes.error,
                },
            }
        }

        const { user } = getUserData()
        const body = {
            ...changes,
            operator_id: user.id,
        }

        try {
            const response = await updateOrder(id, JSON.stringify(body))
            if (!response.data) throw new Error(response.error)

            const updatedOrder = await fetchOrderById(id)
            if (!updatedOrder.data) {
                throw new Error('Не удалось получить данные по заказу')
            }

            commit('UPDATE_ORDER_AFTER_PUT', updatedOrder.data)

            if (updateByButtonClick) {
                commit(
                    'SET_ORIGINAL_ORDER_FIRST_PAYMENT',
                    updatedOrder.data?.order
                )
                commit(
                    'SET_ORIGINAL_CONTRACTOR',
                    updatedOrder.data?.order?.contractor
                )
            }

            commit('SET_WAIT_SAVING', false)

            return {
                order: updatedOrder.data,
                message: {
                    name: 'edit',
                    variant: 'success',
                    message: `Заказ сохранен в ${formatDate(new Date())}.`,
                },
            }
        } catch (error) {
            console.error('Failed to execute putOrder:', error)
            commit('SET_WAIT_SAVING', false)
            throw error
        }
    },

    cancelOrder(context, { id, closeReason, closeComment }) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .patch(
                    `${$themeConfig.app.backUrl}/orders/${id}/close?close_reason_id=${closeReason}`,
                    { close_comment: closeComment }
                )
                .then(response => {
                    resolve(response)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    sendOrder(context, { id }) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .post(`${$themeConfig.app.backUrl}/orders/${id}/send_to_rk`)
                .then(response => {
                    resolve(response)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    copyOrder(context, id) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .post(`${$themeConfig.app.backUrl}/orders/${id}/copy`)
                .then(response => {
                    resolve(response)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    copyAndCloseOrder(context, { orderId, closeReasonId }) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .post(
                    `${$themeConfig.app.backUrl}/orders/${orderId}/close_and_copy?close_reason_id=${closeReasonId}`
                )
                .then(response => {
                    resolve(response)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    getCloseOrderReason(context, { orgId, reasonName }) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .get(
                    `${$themeConfig.app.backUrlDivisions}/organizations/${orgId}/settings?name=${reasonName}`
                )
                .then(response => {
                    resolve(response)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    // Проверить зачем нужно
    updateOrderPayment({ commit }, id) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .patch(`${$themeConfig.app.backUrl}/orders/${id}/payment`)
                .then(response => {
                    commit('UPDATE_PAYMENT', response.data)
                    resolve(response)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    // TODO разобраться, где применяется
    updateDiscount({ commit }, { idPayment, discount }) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .post(`${$themeConfig.app.gqlUrl}?refresh=true`, {
                    query: UPDATE_PAYMENT_DISCOUNT,
                    variables: {
                        discount_id: discount ? discount.id : null,
                        id: idPayment,
                    },
                })
                .then(response => {
                    commit('SET_DISCOUNT', discount)
                    resolve(response)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    fetchAvailabilitiesByRestaurantId({ commit }, { division_id, date }) {
        if (
            !division_id ||
            division_id === '00000000-0000-0000-0000-000000000000'
        ) {
            return Promise.resolve()
        }
        const isoDate = date.toISOString()
        const key = `${division_id}|${normalizeAvailabilityDate(date)}`
        const inFlight = availabilitiesInFlight.get(key)
        if (inFlight) return inFlight

        const getStr = `${$themeConfig.app.backUrl}/availabilities?division_id=${division_id}&date=${isoDate}`
        const request = Vue.axios
            .get(getStr)
            .then(response => {
                commit('SET_AVAILABILITIES', response.data)
            })
            .finally(() => {
                availabilitiesInFlight.delete(key)
            })
        availabilitiesInFlight.set(key, request)
        return request
    },

    // TODO перенести в отдельный модуль stop_list

    setTypeId({ commit, dispatch, state, getters }, val) {
        commit('SET_TYPE', state.types.filter(e => e.id === val)[0])
        if (
            state.originalOrderFirstPaymentType?.payment_type_id !==
                PAYMENT_TYPES.ONLINE &&
            state.originalOrderFirstPaymentType?.payment_type_id !==
                PAYMENT_TYPES.ONLINE_CHANGE &&
            state.originalOrderFirstPaymentType?.payment_type_id !==
                PAYMENT_TYPES.PAYMENT_UNDER_CONTRACT
        ) {
            commit('RESET_PAYMENT_TYPE')
        }

        if (getters.isStaffOrder) {
            commit('SET_DISCOUNT', null)
            commit('ADD_DIRTY', 'dishes')
            commit('ADD_DIRTY', 'promos')
            commit('ADD_DIRTY', 'type')
            commit('ADD_DIRTY', 'first_payment')
        } else {
            commit('SET_ADDRESS', null)
            commit('SET_DISCOUNT', null)
            commit('SET_RESTAURANT_ID', null)
            commit('SET_SECTOR', {})
            commit('SET_INTERVALS', [])
            commit('ADD_DIRTY', 'dishes')
            commit('ADD_DIRTY', 'address')
            commit('ADD_DIRTY', 'promos')
            commit('ADD_DIRTY', 'type')
            commit('ADD_DIRTY', 'restaurant')
            commit('ADD_DIRTY', 'sector')
            commit('ADD_DIRTY', 'first_payment')
            commit('SET_ADDRESS_AVAILABLE', true)
            dispatch('updateTimes', {
                cooking_time: 0,
                delivery_time: 0,
                over_time: 0,
                standard_cooking_time: 0,
                standard_delivery_time: 0,
                timeType: state.order.time_type,
            })
        }
    },

    setBrand({ commit, dispatch }, val) {
        commit('SET_BRAND', val)
        commit('SET_ADDRESS', null)
        commit('SET_RESTAURANT_ID', null)
        commit('DELETE_ALL_DISHES')
        commit('ADD_DIRTY', 'dishes')
        commit('ADD_DIRTY', 'brand')
        commit('ADD_DIRTY', 'restaurant')

        dispatch('refreshDeliveryAvailability')
    },

    setTimeType(
        { commit, dispatch, state },
        timeType,
        intervals = [{ start_time: '12:00:00' }]
    ) {
        if (state.order.time_type === timeType) return

        commit('SET_TIME_TYPE', timeType)

        // Как можно скорее
        if (timeType === TIME_TYPES.asap) {
            commit('SET_DELIVERED_DATE', null)
            commit('SET_DELIVERED_DATE_FORMAT', null)
            commit('ADD_DIRTY', 'delivered_date')
        } else if (timeType === TIME_TYPES.interval) {
            // Интервал
            const date = new Date()
            date.setDate(date.getDate() + 1)

            const parseTime = parseStringTime(intervals[0].start_time)
            date.setHours(parseTime.hour)
            date.setMinutes(parseTime.minutes)
            date.setSeconds(parseTime.seconds)

            commit('SET_DELIVERED_DATE', date)
            commit('ADD_DIRTY', 'delivered_date')
        } else if (timeType === TIME_TYPES.ontime) {
            if (!state.order.delivered_date) {
                commit('SET_DELIVERED_DATE', new Date())
                commit('ADD_DIRTY', 'delivered_date')
            } else if (state.order.order_type?.id === ORDER_TYPES.DELIVERY) {
                const date = new Date()
                const cookingTime = state.order.standard_cooking_time
                const deliveryTime = state.order.standard_delivery_time
                const overTime = state.order.over_time

                const resultDeliveredDate = date.setMinutes(
                    date.getMinutes() +
                        (cookingTime || 0) +
                        (overTime || 0) +
                        (deliveryTime || 0)
                )

                dispatch('updateDeliveredDate', resultDeliveredDate)
            }
        }
        commit('ADD_DIRTY', 'time_type')

        dispatch('refreshDeliveryAvailability')
        dispatch('setExpectedTime', {
            timeType,
            isUpdateDate: true,
        })
    },
    // Исп-ся только для доставки
    setInterval({ dispatch, state }, { interval, updateRestaurant = false }) {
        if (interval) {
            dispatch('updateTimes', {
                cooking_time: 0,
                delivery_time: 0,
                over_time: 0,
                timeType: state.order.time_type,
            })
            if (updateRestaurant) {
                dispatch('setRestaurantByRkId', interval.restaurant_rk_id)
            }
        } else {
            // Нет интервала для Кулинарии
            dispatch('updateTimes', {
                cooking_time: 0,
                delivery_time: 0,
                over_time: 0,
                timeType: state.order.time_type,
            })
        }
    },

    handlerIntervalDelivery({ commit, dispatch, state }, payload) {
        commit('SET_INTERVALS', payload.res || [])
        switch (state.order.time_type) {
            case TIME_TYPES.ontime:
            case TIME_TYPES.interval:
                dispatch('handlerIntervalOntime', payload)
                break

            case TIME_TYPES.asap:
                dispatch('handlerIntervalAsap', payload)
                break

            default:
                break
        }
    },

    // Расчет инт-в когда заказ уже отправлен в подразделение
    handlerPredictedInterval({ dispatch, state, commit }, { intervals }) {
        commit('SET_PREDICTED_INTERVALS', intervals || [])
        switch (state.order.time_type) {
            case TIME_TYPES.ontime:
            case TIME_TYPES.interval:
                dispatch('handlerPredictedIntervalOntime', intervals)
                break

            case TIME_TYPES.asap:
                dispatch('handlerPredictedAsap', intervals)
                break

            default:
                break
        }
    },

    handlerPredictedIntervalOntime({ commit }, intervals) {
        // find an interval where min start_date_time
        const needInterval = intervals
            ? intervals
                  .filter(e => e.start_date_time)
                  .sort((a, b) => {
                      if (a.start_date_time < b.start_date_time) {
                          return -1
                      }
                      if (a.start_date_time > b.start_date_time) {
                          return 1
                      }
                      return 0
                  })[0]
            : null

        if (needInterval) {
            const addedMinutes =
                (needInterval.over_time || 0) +
                (needInterval.cooking_time || 0) +
                (needInterval.delivery_time || 0)
            commit(
                'SET_PREDICTED_DELIVERY_DATE',
                new Date(needInterval.start_date_time)
            )
            commit('SET_PREDICTED_TIME', addedMinutes)
            commit('SET_PREDICTED_AVAILABLE', true)
        } else {
            commit('SET_PREDICTED_DELIVERY_DATE', null)
            commit('SET_PREDICTED_TIME', 0)
            commit('SET_PREDICTED_AVAILABLE', false)
        }
    },

    handlerPredictedAsap({ commit }, intervals) {
        if (intervals) {
            const { cooking_time, over_time, delivery_time } = intervals[0]
            const now = new Date()
            const result_date_time = now.setMinutes(
                now.getMinutes() + cooking_time ||
                    0 + over_time ||
                    0 + delivery_time ||
                    0
            )

            const resultTime =
                cooking_time || 0 + over_time || 0 + delivery_time || 0
            commit('SET_PREDICTED_DELIVERY_DATE', new Date(result_date_time))
            commit('SET_PREDICTED_TIME', resultTime)
            commit('SET_PREDICTED_AVAILABLE', true)
        } else {
            commit('SET_PREDICTED_DELIVERY_DATE', null)
            commit('SET_PREDICTED_TIME', 0)
            commit('SET_PREDICTED_AVAILABLE', false)
        }
    },

    async refreshDeliveryAvailability({ state, dispatch, commit, getters }) {
        commit('SET_DELIVERY_AVAILABILITY_DATA', null)
        commit('SET_ADDRESS_AVAILABLE', true)

        const { permissions } = getUserData()
        const isSeniorOperatorRole = permissions.includes(
            ORDER_IMMEDIATE_SEND_PERMISSION
        )

        const {
            address,
            division_id,
            time_type: timeType,
            order_type: typeOrder,
            brand_id: brandCode,
            delivered_date: deliveryDate,
        } = state.order

        const latitude = parseCoord(address?.latitude)
        const longitude = parseCoord(address?.longitude)
        const restaurantRkId = getRestaurantRkId(state.restaurants, division_id)
        const price = getters.getPrice
        const dishType = getDishType(timeType)

        // ===== Обработчики =====

        const handleAsapDeliveryV2 = async () => {
            const response = await callApi(dispatch, 'fetchDeliveryIntervals', {
                timeType,
                brandCode,
                dishType,
                latitude,
                longitude,
                price,
            })
            if (!response) return

            applyBaseCommits(commit, response.data)
            const {
                is_available,
                intervals,
                order_min_sum: minSum,
                delivered_date_time: deliveryDateTime,
            } = response.data

            commit(
                'SET_ADDRESS_AVAILABLE',
                is_available || intervals?.length > 0
            )
            commit(
                'SET_MIN_SUM',
                typeOrder.id === ORDER_TYPES.SUBDELIVERY ? 0 : minSum
            )

            if (
                canProceedWithIntervals(
                    is_available,
                    intervals,
                    isSeniorOperatorRole
                )
            ) {
                if (intervals?.[0]) {
                    const { restaurant_rk_id, sector_id } = intervals[0]
                    const restaurant = state.restaurants.find(
                        r => r.rk_id === restaurant_rk_id
                    )

                    commit('SET_RESTAURANT_ID', restaurant?.id || null)
                    commit('ADD_DIRTY', 'restaurant')
                    commit('ADD_DIRTY', 'sector')

                    await dispatch('setSectorById', sector_id)
                    await dispatch('refreshStopListsAndAvailabilities', {
                        restaurantId: restaurant?.id,
                        deliveredDate: deliveryDateTime
                            ? new Date(deliveryDateTime)
                            : new Date(),
                    })
                }
                await dispatch('handlerIntervalAsap', { res: intervals })
                await dispatch('setExpectedTime', {
                    timeType,
                    isUpdateDate: true,
                })
            } else {
                commit('SET_DELIVERED_DATE', null)
                commit('ADD_DIRTY', 'delivered_date')
                await dispatch('handlerIntervalAsap', { res: null })
            }
        }

        const handleAsapSelfV2 = async () => {
            if (!restaurantRkId) {
                await dispatch('handlerAsap', { res: null })
                return
            }

            const response = await callApi(dispatch, 'fetchSelfIntervals', {
                timeType,
                brandCode,
                dishType,
                restaurantRkId,
                price,
            })
            if (!response) return

            applyBaseCommits(commit, response.data)
            const { is_available, intervals } = response.data

            const canProceed = canProceedWithIntervals(
                is_available,
                intervals,
                isSeniorOperatorRole
            )
            await dispatch('handlerAsap', {
                res: canProceed ? intervals : null,
            })
        }

        const handleOnTimeDeliveryV2 = async () => {
            const estimatedDate = convertToTZformat(deliveryDate)
            const response = await callApi(dispatch, 'fetchDeliveryIntervals', {
                timeType,
                brandCode,
                dishType,
                latitude,
                longitude,
                price,
                estimatedDate,
            })
            if (!response) return

            applyBaseCommits(commit, response.data)
            const {
                is_available,
                intervals,
                order_min_sum: minSum,
                delivered_date_time: deliveryDateTime,
            } = response.data

            commit(
                'SET_ADDRESS_AVAILABLE',
                is_available || intervals?.length > 0
            )
            commit(
                'SET_MIN_SUM',
                typeOrder.id === ORDER_TYPES.SUBDELIVERY ? 0 : minSum
            )

            if (
                canProceedWithIntervals(
                    is_available,
                    intervals,
                    isSeniorOperatorRole
                )
            ) {
                if (getters.getSectorsForSelect[0]) {
                    dispatch(
                        'setSectorById',
                        getters.getSectorsForSelect[0]?.id
                    )
                    commit('ADD_DIRTY', 'sector')
                }

                commit(
                    'SET_RESTAURANT_ID',
                    getters.getRestaurantsForSelect[0]?.id || null
                )
                commit('ADD_DIRTY', 'restaurant')

                await dispatch('refreshStopListsAndAvailabilities', {
                    restaurantId: getters.getRestaurantsForSelect[0]?.id,
                    deliveredDate: deliveryDateTime
                        ? new Date(deliveryDateTime)
                        : new Date(),
                })

                await dispatch('handlerIntervalOntime', {
                    res: getters.getIntervals?.length ? intervals : null,
                    updateRestaurant: true,
                })
            } else {
                await dispatch('handlerIntervalOntime', { res: null })
            }
        }

        const handleIntervalDeliveryV2 = async () => {
            const estimatedDate = convertToTZformat(deliveryDate)
            const response = await callApi(dispatch, 'fetchDeliveryIntervals', {
                timeType,
                brandCode,
                dishType,
                latitude,
                longitude,
                price,
                estimatedDate,
            })
            if (!response) return

            applyBaseCommits(commit, response.data)
            const {
                is_available,
                intervals,
                order_min_sum: minSum,
                delivered_date_time: deliveryDateTime,
            } = response.data

            commit('SET_ADDRESS_AVAILABLE', is_available)
            commit(
                'SET_MIN_SUM',
                typeOrder.id === ORDER_TYPES.SUBDELIVERY ? 0 : minSum
            )

            if (is_available) {
                if (getters.getSectorsForSelect[0]) {
                    dispatch(
                        'setSectorById',
                        getters.getSectorsForSelect[0]?.id
                    )
                    commit('ADD_DIRTY', 'sector')
                }

                commit(
                    'SET_RESTAURANT_ID',
                    getters.getRestaurantsForSelect[0]?.id || null
                )
                commit('ADD_DIRTY', 'restaurant')

                await dispatch('refreshStopListsAndAvailabilities', {
                    restaurantId: getters.getRestaurantsForSelect[0]?.id,
                    deliveredDate: deliveryDateTime
                        ? new Date(deliveryDateTime)
                        : new Date(),
                })

                await dispatch('handlerIntervalOntime', {
                    res: intervals,
                    updateRestaurant: true,
                })
            } else {
                await dispatch('handlerIntervalOntime', { res: null })
            }
        }

        const handleOntimeSelfV2 = async () => {
            if (!deliveryDate || !restaurantRkId) {
                await dispatch('handlerIntervalOntime', { res: null })
                return
            }

            const estimatedDate = convertToTZformat(deliveryDate)
            const response = await callApi(dispatch, 'fetchSelfIntervals', {
                timeType,
                brandCode,
                dishType,
                restaurantRkId,
                price,
                estimatedDate,
            })
            if (!response) return

            applyBaseCommits(commit, response.data)
            const { is_available, intervals } = response.data

            const canProceed = canProceedWithIntervals(
                is_available,
                intervals,
                isSeniorOperatorRole
            )
            await dispatch('handlerIntervalOntime', {
                res: canProceed ? intervals : null,
            })
        }

        const handlePreorderAsap = async () => {
            if (!restaurantRkId) {
                await dispatch('handlerAsap', { res: null })
                return
            }

            const response = await callApi(dispatch, 'fetchPreOrderIntervals', {
                timeType,
                brandCode,
                dishType,
                restaurantRkId,
                price,
            })
            if (!response) return

            applyBaseCommits(commit, response.data)
            const { is_available, intervals } = response.data

            await dispatch('handlerAsap', {
                res: is_available ? intervals : null,
            })
        }

        // ===== Выбор обработчика =====
        if (
            timeType === TIME_TYPES.asap &&
            [ORDER_TYPES.DELIVERY, ORDER_TYPES.SUBDELIVERY].includes(
                typeOrder.id
            )
        ) {
            await handleAsapDeliveryV2()
        } else if (
            timeType === TIME_TYPES.asap &&
            typeOrder.id === ORDER_TYPES.SELF
        ) {
            await handleAsapSelfV2()
        } else if (
            timeType === TIME_TYPES.ontime &&
            [ORDER_TYPES.DELIVERY, ORDER_TYPES.SUBDELIVERY].includes(
                typeOrder.id
            )
        ) {
            await handleOnTimeDeliveryV2()
        } else if (
            timeType === TIME_TYPES.interval &&
            [ORDER_TYPES.DELIVERY, ORDER_TYPES.SUBDELIVERY].includes(
                typeOrder.id
            )
        ) {
            await handleIntervalDeliveryV2()
        } else if (
            timeType === TIME_TYPES.ontime &&
            typeOrder.id === ORDER_TYPES.SELF
        ) {
            await handleOntimeSelfV2()
        } else if (
            timeType === TIME_TYPES.asap &&
            typeOrder.id === ORDER_TYPES.PREORDER
        ) {
            await handlePreorderAsap()
        }
    },

    setRestaurantByRkId({ commit, state, dispatch }, restaurantRkId) {
        const restaurant = state.restaurants.find(
            e => e.rk_id === restaurantRkId
        )

        commit('SET_RESTAURANT_ID', restaurant?.id || null)
        commit('ADD_DIRTY', 'restaurant')

        // refreshStopListsAndAvailabilities
        dispatch('refreshStopListsAndAvailabilities', {
            restaurantId: restaurant?.id || null,
            deliveredDate: state.order.delivered_date
                ? new Date(state.order.delivered_date)
                : new Date(),
        })
    },

    setRestaurantById({ commit, dispatch, state }, id) {
        commit('ADD_DIRTY', 'restaurant')
        commit('SET_RESTAURANT_ID', id)

        dispatch('refreshDeliveryAvailability')
        dispatch('setExpectedTime', {
            timeType: state.order.time_type,
            isUpdateDate: false,
        })
        dispatch('fetchCommentByRestaurantId', id)

        // refreshStopListsAndAvailabilities
        dispatch('refreshStopListsAndAvailabilities', {
            restaurantId: id,
            deliveredDate: state.order.delivered_date
                ? new Date(state.order.delivered_date)
                : new Date(),
        })
    },

    refreshStopListsAndAvailabilities(
        { dispatch },
        { restaurantId, deliveredDate }
    ) {
        dispatch('fetchStopListsByRestaurantId', restaurantId)
        dispatch('fetchAvailabilitiesByRestaurantId', {
            division_id: restaurantId,
            date: deliveredDate,
        })
    },

    updateTimes(
        { commit, dispatch },
        {
            cooking_time,
            delivery_time,
            over_time,
            timeType,
            standard_cooking_time,
            standard_delivery_time,
        }
    ) {
        commit('SET_COOKING_TIME', cooking_time || 0)
        commit('SET_DELIVERY_TIME', delivery_time || 0)
        commit('SET_OVER_TIME', over_time || 0)
        commit('SET_STANDARD_COOKING_TIME', standard_cooking_time || 0)
        commit('SET_STANDARD_DELIVERY_TIME', standard_delivery_time || 0)

        commit('ADD_DIRTY', 'claimed_cooking_time')
        commit('ADD_DIRTY', 'claimed_delivery_time')
        commit('ADD_DIRTY', 'claimed_over_time')

        dispatch('setExpectedTime', {
            timeType,
            isUpdateDate: false,
        })
    },

    setSectorById({ commit, state }, id) {
        const findedSector = state.sectors.find(e => e.id === id)
        if (findedSector) {
            commit('SET_SECTOR', { name: findedSector.name, id })
        } else {
            commit('SET_SECTOR', { id })
        }
    },

    canSetExpectedTime({ state, getters }) {
        const isLockedByUser = state.order.lock_by_user
        const hasCancelDate = getters.getAllDatesOrder.statusCancelDate

        return !(
            isLockedByUser ||
            hasCancelDate ||
            getters.isBeyondWaitingStage
        )
    },

    // setExpectedTime
    setExpectedTime({ commit, state, dispatch }, { timeType, isUpdateDate }) {
        dispatch('canSetExpectedTime').then(res => {
            if (!res) return

            const cookingTime =
                state.order.cooking_time ||
                state.order.order_time?.claimed_cooking_time ||
                state.order.order_time?.actual_cooking_time
            const deliveryTime =
                state.order.delivery_time ||
                state.order.order_time?.claimed_delivery_time ||
                state.order.order_time?.actual_delivery_time
            const overTime =
                state.order.over_time ||
                state.order.order_time?.claimed_over_time ||
                state.order.order_time?.actual_over_time
            const deliveredDate = state.order.delivered_date

            switch (timeType) {
                case 'asap': {
                    const hasTime =
                        (cookingTime || 0) +
                            (deliveryTime || 0) +
                            (overTime || 0) >
                        0
                    if (!hasTime) {
                        break
                    }

                    const date = new Date()
                    date.setTime(
                        date.getTime() +
                            1000 *
                                60 *
                                ((cookingTime || 0) +
                                    (deliveryTime || 0) +
                                    (overTime || 0))
                    )

                    if (isUpdateDate) {
                        commit('SET_DELIVERED_DATE', date)
                        commit('ADD_DIRTY', 'delivered_date')
                        commit('TRIGGER_UPDATE_DATE', true)
                    }
                    break
                }
                case 'ontime': {
                    const deliveredDateFormat = formatDateForPickr(
                        new Date(deliveredDate)
                    )
                    commit('SET_DELIVERED_DATE_FORMAT', deliveredDateFormat)
                    break
                }
                case 'interval':
                    // TODO is_preorder
                    break

                default:
                    break
            }
        })
    },
    // For input from form
    setDeliveredDate({ commit, dispatch }, val) {
        const date = parseStringToDate(val)
        commit('SET_DELIVERED_DATE', date.toISOString())
        commit('SET_DELIVERED_DATE_FORMAT', val)
        commit('ADD_DIRTY', 'delivered_date')
        dispatch('refreshDeliveryAvailability')
    },

    updateDeliveredDate({ commit }, result_date_time) {
        const resultDate = new Date(result_date_time)
        const deliveredDateFormat = formatDateForPickr(resultDate)

        commit('SET_DELIVERED_DATE', resultDate.toISOString())
        commit('SET_DELIVERED_DATE_FORMAT', deliveredDateFormat)
        commit('ADD_DIRTY', 'delivered_date')
    },

    /**
     * Загружает комментарии и причины изменения времени для ресторана
     * @param {Object} context - Vuex context
     * @param {string} restaurantId - ID ресторана
     * @returns {Promise<Object>} Ответ от API
     */
    async fetchCommentByRestaurantId({ commit, state }, restaurantId) {
        const restaurant = state.restaurants.find(r => r.id === restaurantId)

        if (!restaurant?.rk_id) {
            console.warn(`Restaurant ${restaurantId} not found or has no rk_id`)
            return null
        }

        try {
            const { data } = await Vue.axios.get(
                `${$themeConfig.app.backUrlDeliveryApiV1}/division/${restaurant.rk_id}/time`
            )

            const {
                cooking_time: cookingTimeData,
                delivery_time: deliveryTimeData,
            } = data
            const selectedSectorId = state.order?.sector_id

            const sectorDeliveryData = deliveryTimeData?.find(
                item => item.sector_id === selectedSectorId
            )

            commit('SET_REASON_CHANGE_TIME', {
                cooking_comment: cookingTimeData?.comment ?? null,
                cooking_time_change_reason:
                    cookingTimeData?.change_reason ?? null,
                delivery_comment: sectorDeliveryData?.comment ?? null,
                delivery_time_change_reason:
                    sectorDeliveryData?.change_reason ?? null,
            })

            return data
        } catch (error) {
            console.error('Failed to fetch comment by restaurant:', error)
            throw error
        }
    },

    fetchReasonsChangeTime({ commit, state }) {
        return new Promise((resolve, reject) => {
            if (!state.reasonChanges) {
                Vue.axios
                    .post(`${$themeConfig.app.gqlUrl}`, {
                        query: GET_REASON_TICKET_TIME,
                    })
                    .then(response => {
                        commit(
                            'SET_REASON_CHANGES',
                            response.data.data.ticket_time_change_reason
                        )
                        resolve(response.data)
                    })
                    .catch(error => {
                        reject(error)
                    })
            } else {
                resolve()
            }
        })
    },

    resendOrder({ commit }, orderId) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .post(
                    `${$themeConfig.app.backUrl}/orders/${orderId}/resend_to_rk`
                )
                .then(response => {
                    resolve(response)
                    commit('SET_HAS_RESEND_DISHES', false)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },
    createSubdelivery(ctx, { orderId, comment, dishes }) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .post(
                    `${$themeConfig.app.backUrl}/orders/${orderId}/subdelivery`,
                    { comment, dishes }
                )
                .then(response => {
                    resolve(response.data)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    /**
     * Recalculates the delivered date after send to RK
     */
    async recalcDeliveredDate({ state, dispatch, commit, getters }) {
        const {
            address,
            division_id,
            time_type: timeType,
            order_type,
            delivered_date,
            brand_id: brandCode,
        } = state.order

        const latitude = parseCoord(address?.latitude)
        const longitude = parseCoord(address?.longitude)
        const restaurantRkId = getRestaurantRkId(state.restaurants, division_id)
        const price = getters.getPrice
        const dishType = getDishType(timeType)

        // ===== Обработчики =====

        const handleAsapDelivery = async () => {
            const response = await callApi(dispatch, 'fetchDeliveryIntervals', {
                timeType,
                brandCode,
                dishType,
                latitude,
                longitude,
                price,
            })
            if (!response) return

            commit('SET_DELIVERY_AVAILABILITY_DATA', response.data)
            const { is_available, intervals } = response.data

            if (is_available) {
                await dispatch('handlerPredictedInterval', { intervals })
            }
        }

        const handleAsapSelf = async () => {
            if (!restaurantRkId) {
                console.error('Как можно скорее. Самовывоз', 'Пустой RK id')
                return
            }

            const response = await callApi(dispatch, 'fetchSelfIntervals', {
                timeType,
                brandCode,
                dishType,
                restaurantRkId,
                price,
            })
            if (!response) return

            commit('SET_DELIVERY_AVAILABILITY_DATA', response.data)
            const { is_available, intervals } = response.data

            await dispatch('handlerPredictedInterval', {
                intervals: is_available ? intervals : null,
            })
        }

        const handleOntimeDelivery = async () => {
            const estimatedDate = convertToTZformat(delivered_date)
            const response = await callApi(dispatch, 'fetchDeliveryIntervals', {
                timeType,
                brandCode,
                dishType,
                latitude,
                longitude,
                price,
                estimatedDate,
            })
            if (!response) return

            commit('SET_DELIVERY_AVAILABILITY_DATA', response.data)
            const { is_available, intervals } = response.data

            if (is_available) {
                await dispatch('handlerPredictedInterval', { intervals })
            }
        }

        const handleOntimeSelf = async () => {
            if (!delivered_date || !restaurantRkId) return

            const estimatedDate = convertToTZformat(delivered_date)
            const response = await callApi(dispatch, 'fetchSelfIntervals', {
                timeType,
                brandCode,
                dishType,
                restaurantRkId,
                price,
                estimatedDate,
            })
            if (!response) return

            commit('SET_DELIVERY_AVAILABILITY_DATA', response.data)
            const { is_available, intervals } = response.data

            await dispatch('handlerPredictedInterval', {
                intervals: is_available ? intervals : null,
            })
        }

        const handleAsapPreOrder = async () => {
            if (!restaurantRkId) {
                console.error('Как можно скорее. Предзаказ', 'Пустой RK id')
                return
            }

            const response = await callApi(dispatch, 'fetchPreOrderIntervals', {
                timeType,
                brandCode,
                dishType,
                restaurantRkId,
                price,
            })
            if (!response) return

            commit('SET_DELIVERY_AVAILABILITY_DATA', response.data)
            const { is_available, intervals } = response.data

            await dispatch('handlerPredictedInterval', {
                intervals: is_available ? intervals : null,
            })
        }

        // ===== Выбор обработчика =====
        if (timeType === TIME_TYPES.asap) {
            if (
                [ORDER_TYPES.DELIVERY, ORDER_TYPES.SUBDELIVERY].includes(
                    order_type.id
                )
            ) {
                await handleAsapDelivery()
            } else if (order_type.id === ORDER_TYPES.SELF) {
                await handleAsapSelf()
            } else if (order_type.id === ORDER_TYPES.PREORDER) {
                await handleAsapPreOrder()
            }
        } else if (timeType === TIME_TYPES.ontime) {
            if (
                [ORDER_TYPES.DELIVERY, ORDER_TYPES.SUBDELIVERY].includes(
                    order_type.id
                )
            ) {
                await handleOntimeDelivery()
            } else if (order_type.id === ORDER_TYPES.SELF) {
                await handleOntimeSelf()
            }
        }
    },

    canselChanges({ dispatch, commit }, orderId) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .patch(
                    `${$themeConfig.app.backUrl}/orders/${orderId}/revert_changes`
                )
                .then(() => {
                    dispatch('fetchOrder', orderId)
                    commit('SET_HAS_RESEND_DISHES', false)
                    commit('SET_RESEND_DISHES', null)
                    resolve()
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    // setters
    setMode({ commit }, payload) {
        commit('SET_MODE', payload)
    },

    setLongitude({ commit }, val) {
        commit('SET_LONGITUDE', val)
    },

    setLatitude({ commit }, val) {
        commit('SET_LATITUDE', val)
    },

    setRawAddress({ commit }, val) {
        commit('SET_RAW_ADDRESS', val)
    },

    handlerIntervalOntime(
        { dispatch, state },
        { res: deliveryApiIntervals, updateRestaurant = false }
    ) {
        const needInterval = deliveryApiIntervals?.[0] || null

        if (needInterval) {
            dispatch('updateTimes', {
                cooking_time: needInterval.cooking_time,
                delivery_time: needInterval.delivery_time, // для самовывоза будет 0, это нормально
                over_time: needInterval.over_time,
                standard_cooking_time: needInterval.standard_cooking_time,
                standard_delivery_time: needInterval.standard_delivery_time,
                timeType: state.order.time_type,
            })

            if (updateRestaurant) {
                dispatch('setRestaurantByRkId', needInterval.restaurant_rk_id)
            }
        } else {
            dispatch('updateTimes', {
                cooking_time: 0,
                delivery_time: 0,
                over_time: 0,
                standard_cooking_time: 0,
                standard_delivery_time: 0,
                timeType: state.order.time_type,
            })
        }
    },
    handlerIntervalAsap({ dispatch, state }, { res: deliveryApiIntervals }) {
        if (deliveryApiIntervals && deliveryApiIntervals[0]) {
            const needInterval = deliveryApiIntervals[0]
            dispatch('updateTimes', {
                cooking_time: needInterval.cooking_time,
                delivery_time: needInterval.delivery_time,
                over_time: needInterval.over_time,
                standard_cooking_time: needInterval.standard_cooking_time,
                standard_delivery_time: needInterval.standard_delivery_time,
                timeType: state.order.time_type,
            })
        } else {
            dispatch('updateTimes', {
                cooking_time: 0,
                delivery_time: 0,
                over_time: 0,
                standard_cooking_time: 0,
                standard_delivery_time: 0,
                timeType: state.order.time_type,
            })
        }
    },

    handlerAsap(
        { commit, dispatch, state, getters },
        { res: dataAsap, updateRestaurant = false }
    ) {
        let cookingTime = 0
        let deliveryTime = 0
        let overTime = 0

        if (dataAsap?.length && dataAsap[0]?.restaurant_rk_id) {
            const firstDataAsap = dataAsap[0]
            const { cooking_time, over_time, restaurant_rk_id, delivery_time } =
                firstDataAsap
            const now = new Date()

            cookingTime = cooking_time
            deliveryTime = delivery_time
            overTime = over_time

            if (!getters.isBeyondWaitingStage) {
                const result_date_time = now.setMinutes(
                    now.getMinutes() +
                        (cookingTime || 0) +
                        (overTime || 0) +
                        (deliveryTime || 0)
                )

                dispatch('updateDeliveredDate', result_date_time)

                if (updateRestaurant)
                    dispatch('setRestaurantByRkId', restaurant_rk_id)
            }
        } else {
            commit('SET_DELIVERED_DATE', null)
            commit('ADD_DIRTY', 'delivered_date')
        }

        dispatch('updateTimes', {
            cooking_time: cookingTime,
            delivery_time: deliveryTime,
            over_time: overTime,
            timeType: state.order.time_type,
        })
    },

    async deleteSecondPayment({ commit, getters, dispatch }) {
        const hasSecondPaymentBeenSaved = Boolean(
            'id' in getters.getSecondPayment
        )
        const orderId = getters.getOrderId
        const finalOrderPrice = getters.getDishesFinalPrice

        if (hasSecondPaymentBeenSaved) {
            commit('SET_EMPTY_SECOND_PAYMENT', finalOrderPrice)
            await dispatch('putOrder', { id: orderId })
            return
        }
        commit('DELETE_SECOND_PAYMENT')
    },

    unblockOrder(_, id) {
        return Vue.axios
            .post(`${$themeConfig.app.backUrl}/orders/${id}/unblock`)
            .catch(error => {
                console.error(error)
                throw error
            })
    },

    // Получение интервалов для доставки
    fetchDeliveryIntervals(
        ctx,
        {
            timeType,
            brandCode,
            dishType = 'standard',
            latitude,
            longitude,
            price,
            estimatedDate,
        }
    ) {
        const payload = buildIntervalsPayload({
            timeType,
            brandCode,
            dishType,
            price,
            estimatedDate,
            locationParams: buildCoordinatesParams(latitude, longitude),
        })

        return fetchIntervals(payload, ORDER_TYPES.DELIVERY)
    },

    // Получение интервалов для самовывоза
    fetchSelfIntervals(
        ctx,
        {
            timeType,
            brandCode,
            dishType = 'standard',
            restaurantRkId,
            price,
            estimatedDate,
        }
    ) {
        const payload = buildIntervalsPayload({
            timeType,
            brandCode,
            dishType,
            price,
            estimatedDate,
            locationParams: buildRestaurantParams(restaurantRkId),
        })

        return fetchIntervals(payload, ORDER_TYPES.SELF)
    },

    // Получение интервалов для предзаказов
    fetchPreOrderIntervals(
        ctx,
        { timeType, brandCode, dishType = 'standard', restaurantRkId, price }
    ) {
        const payload = buildIntervalsPayload({
            timeType,
            brandCode,
            dishType,
            price,
            locationParams: buildRestaurantParams(restaurantRkId),
        })

        return fetchIntervals(payload, ORDER_TYPES.PREORDER)
    },

    /**
     * Перепроверяет возможность отправки заказа
     * @returns {Object} Информация о доступности отправки.
     * @property {boolean} isAvailable - флаг доступности отправки.
     * @property {string} reason - причина недоступности отправки заказа.
     */
    async revalidateAndPrepareOrder({ dispatch, state, getters }) {
        try {
            // 1. Вызываем action, который получит данные и положит их в state.deliveryAvailabilityData
            await dispatch('refreshDeliveryAvailability')

            // 2. Получаем сырые данные из state для анализа.
            const deliveryData = state.deliveryAvailabilityData

            // Проверка №1 (самая базовая): Получили ли мы вообще какие-то данные?
            if (!deliveryData) {
                console.error(
                    'Validation failed: deliveryAvailabilityData is null.'
                )
                return {
                    isAvailable: false,
                    reason: 'Не были получены данные по заказу.',
                }
            }

            // Проверка №2: Есть ли в полученных данных хоть какие-то интервалы?
            const hasIntervals =
                deliveryData.intervals && deliveryData.intervals.length > 0
            if (!hasIntervals) {
                console.error('Validation failed: intervals are empty.')
                return {
                    isAvailable: false,
                    reason: 'Нет доступных интервалов.',
                }
            }

            // Проверка №3: Учитываем права оператора и флаг is_available
            const { permissions } = getUserData()
            const isSeniorOperator = permissions.includes(
                ORDER_IMMEDIATE_SEND_PERMISSION
            )

            // Если API сказало, что интервалы "неактивные" (`is_available: false`),
            // то только старший оператор может продолжить.
            if (!deliveryData.is_available && !isSeniorOperator) {
                // Сообщение пользователю уже должно было появиться в виде плашки
                // "Ближайший интервал...". Здесь мы просто блокируем отправку.
                console.error('Validation failed: is_available is false.')
                return {
                    isAvailable: false,
                    reason: 'Ресторан/цех неактивен в данное время.',
                }
            }

            // Проверка №4 (финальная "санитарная" проверка): Убедимся, что после всех манипуляций
            // в `refreshDeliveryAvailability` состояние заказа осталось валидным.
            // Эти геттеры будут пересчитаны на основе state, который обновил refreshDeliveryAvailability.
            const isTimeValid = getters.hasTicketTime
            const isMinSumOk = getters.isMinSumReached
            const isDivisionSet = !!state.order.division_id

            if (!isTimeValid || !isMinSumOk || !isDivisionSet) {
                console.error('Order state is invalid after revalidation', {
                    isTimeValid,
                    isMinSumOk,
                    isDivisionSet,
                })
                return {
                    isAvailable: false,
                    reason: 'Проверьте, есть ли ожидаемое время, установлен ли цех и пройдена ли проверка по минимальной сумме.',
                }
            }

            // Если мы прошли все проверки - заказ готов к отправке!
            return {
                isAvailable: true,
                reason: null,
            }
        } catch (error) {
            console.error('Failed to execute revalidateAndPrepareOrder:', error)
            // При любой ошибке в цепочке вызовов - запрещаем отправку.
            return {
                isAvailable: false,
                reason: 'Что-то пошло не так. Повторите позднее.',
            }
        }
    },
}
